import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Gavel, Timer, TrendingUp, HandCoins, Truck, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/*  Countdown helper                                                          */
/* -------------------------------------------------------------------------- */

function useCountdown(endIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endIso]);

  return useMemo(() => {
    if (!endIso) return { over: true, label: "—", tight: false };
    const end = new Date(endIso).getTime();
    const ms = end - now;
    if (ms <= 0) return { over: true, label: "Ended", tight: true };
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    let label;
    if (d > 0) label = `${d}d ${h}h ${m}m`;
    else if (h > 0) label = `${h}h ${m}m ${sec}s`;
    else if (m > 0) label = `${m}m ${sec}s`;
    else label = `${sec}s`;
    return { over: false, label, tight: s < 300, urgent: s < 120 };
  }, [endIso, now]);
}

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

/* -------------------------------------------------------------------------- */
/*  <AuctionPanel /> — countdown, bid form, bid history                       */
/* -------------------------------------------------------------------------- */

export function AuctionPanel({ listing, onUpdate }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const cd = useCountdown(listing?.ends_at);
  const [bidHistory, setBidHistory] = useState({ items: [], min_next_bid_cents: 0, reserve_met: null });
  const [bidDollars, setBidDollars] = useState("");
  const [placing, setPlacing] = useState(false);
  const [hasPm, setHasPm] = useState(null);

  const isSeller = user?.id === listing?.seller_id;
  const isEnded = cd.over || listing?.status !== "active";

  const refreshHistory = async () => {
    try {
      const { data } = await api.get(`/marketplace/listings/${listing.id}/bids`);
      setBidHistory(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!listing?.id || listing?.listing_type !== "auction") return;
    refreshHistory();
    // Poll bid history every 15s so buyers see live updates without WebSockets.
    const t = setInterval(refreshHistory, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id]);

  useEffect(() => {
    if (!user || isSeller) return;
    (async () => {
      try {
        const { data } = await api.get("/marketplace/payment-methods/mine");
        setHasPm(!!data?.has_payment_method);
      } catch { setHasPm(false); }
    })();
  }, [user, isSeller]);

  const placeBid = async () => {
    if (!user) { nav("/login"); return; }
    const cents = Math.round(parseFloat(bidDollars || "0") * 100);
    if (!cents || cents < bidHistory.min_next_bid_cents) {
      toast.error(`Minimum next bid is ${fmt(bidHistory.min_next_bid_cents)}`);
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${listing.id}/bid`, { amount_cents: cents });
      toast.success(data.extended ? "Bid placed! Auction extended by 2 minutes." : "Bid placed!");
      setBidDollars("");
      await refreshHistory();
      if (onUpdate) onUpdate(data.listing);
    } catch (e) {
      const detail = e.response?.data?.detail || "Bid failed";
      if (e.response?.status === 402) {
        toast.error("Save a payment method first — your card is auto-charged only if you win.");
        setHasPm(false);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setPlacing(false); }
  };

  if (listing?.listing_type !== "auction") return null;

  return (
    <Card className="rounded-xl border border-[#EAE3DE] bg-white p-5 space-y-4" data-testid="auction-panel">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-[#B31E1E] text-white px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider">
          <Gavel size={11} /> Live auction
        </div>
        {listing.reserve_cents && (
          <span className={`text-[11px] font-mono px-2 py-0.5 rounded ${
            bidHistory.reserve_met ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"
          }`} data-testid="auction-reserve-status">
            {bidHistory.reserve_met ? "Reserve met" : "Reserve not met"}
          </span>
        )}
      </div>

      {/* Current bid + countdown grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-[#FAF5F2] p-3">
          <div className="text-[10px] font-mono uppercase text-[#8B7B73]">Current bid</div>
          <div className="mt-1 text-3xl font-black tabular-nums text-[#1B1614]" data-testid="auction-current-bid">
            {fmt(listing.current_bid_cents)}
          </div>
          <div className="text-[11px] text-[#8B7B73]">{listing.bid_count || 0} bids</div>
        </div>
        <div className={`rounded-lg p-3 ${cd.urgent ? "bg-red-50 border border-red-200" : "bg-[#FAF5F2]"}`}>
          <div className="text-[10px] font-mono uppercase text-[#8B7B73] flex items-center gap-1"><Timer size={10} /> {cd.over ? "Status" : "Time left"}</div>
          <div className={`mt-1 text-2xl font-black tabular-nums ${cd.urgent ? "text-red-700" : "text-[#1B1614]"}`} data-testid="auction-countdown">
            {cd.label}
          </div>
          {cd.tight && !cd.over && <div className="text-[10px] text-red-700 font-medium">Anti-snipe active</div>}
        </div>
      </div>

      {/* Highest-bidder-is-me banner */}
      {listing.highest_bidder_is_me && !isEnded && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800 flex items-center gap-2" data-testid="auction-you-lead">
          <CheckCircle2 size={13} /> You&apos;re the highest bidder — hold your position.
        </div>
      )}

      {/* Bid form */}
      {!isSeller && !isEnded && (
        <div className="space-y-2">
          <Label htmlFor="bid-amount" className="text-xs">Your bid (USD)</Label>
          <div className="flex gap-2">
            <Input
              id="bid-amount"
              type="number"
              step="0.01"
              min={bidHistory.min_next_bid_cents / 100}
              value={bidDollars}
              onChange={(e) => setBidDollars(e.target.value)}
              placeholder={`${(bidHistory.min_next_bid_cents / 100).toFixed(2)} or more`}
              className="rounded-md"
              data-testid="auction-bid-input"
            />
            <Button
              onClick={placeBid}
              disabled={placing}
              className="rounded-md bg-[#B31E1E] hover:bg-[#8F1717] text-white font-semibold shrink-0"
              data-testid="auction-place-bid-btn"
            >
              {placing ? "Placing…" : (
                <><TrendingUp size={14} className="mr-1.5" /> Place bid</>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-[#8B7B73]">
            Minimum next bid: <span className="font-mono">{fmt(bidHistory.min_next_bid_cents)}</span>.
            {" "}Your card is only charged if you win.
          </p>
          {hasPm === false && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="auction-no-pm-warning">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>
                No card on file. On your first bid we&apos;ll ask you to save one via Stripe — it&apos;s only charged if you win the auction.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Bid history */}
      {bidHistory.items?.length > 0 && (
        <details className="rounded-lg border border-[#EAE3DE] bg-[#FAF5F2]" data-testid="auction-bid-history">
          <summary className="cursor-pointer px-3 py-2 text-[12px] font-semibold text-[#1B1614] select-none">
            Bid history ({bidHistory.items.length})
          </summary>
          <ul className="divide-y divide-[#EAE3DE] max-h-56 overflow-auto">
            {bidHistory.items.map((b, i) => (
              <li key={b.id} className="px-3 py-2 flex items-center justify-between text-[12px]" data-testid={`auction-bid-row-${i}`}>
                <span className="text-[#5C4E48]">{b.bidder_masked || "Anonymous"}</span>
                <span className="font-mono font-semibold text-[#1B1614]">{fmt(b.amount_cents)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  <MakeOfferButton /> — fixed-price listings with accepts_offers=true       */
/* -------------------------------------------------------------------------- */

export function MakeOfferButton({ listing, onSubmitted }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [amountDollars, setAmountDollars] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!listing?.accepts_offers || listing.listing_type !== "fixed" || listing.status !== "active") return null;
  if (user?.id === listing.seller_id) return null;

  const submit = async () => {
    if (!user) { nav("/login"); return; }
    const cents = Math.round(parseFloat(amountDollars || "0") * 100);
    if (!cents || cents < 100) return toast.error("Enter an offer of at least $1.00");
    if (cents >= listing.price_cents) return toast.error("Offer must be less than the asking price — or just Buy Now.");
    setSending(true);
    try {
      await api.post(`/marketplace/listings/${listing.id}/offer`, {
        amount_cents: cents,
        message: message.trim(),
      });
      toast.success("Offer sent — the seller has 48h to respond.");
      setOpen(false);
      setAmountDollars("");
      setMessage("");
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-[35px]" data-testid="mp-make-offer-btn">
          <HandCoins size={14} className="mr-2" /> Make an offer
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="mp-make-offer-dialog">
        <DialogHeader>
          <DialogTitle>Make an offer</DialogTitle>
          <DialogDescription>
            Asking price: <span className="font-mono font-semibold">{fmt(listing.price_cents)}</span>.
            The seller can accept, reject, or counter. Offers expire in 48h.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="offer-amount" className="text-xs">Your offer (USD)</Label>
            <Input
              id="offer-amount"
              type="number" step="0.01" min={1}
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              placeholder={`less than ${fmt(listing.price_cents)}`}
              className="rounded-md mt-1"
              data-testid="mp-offer-amount-input"
            />
          </div>
          <div>
            <Label htmlFor="offer-message" className="text-xs">Message (optional)</Label>
            <Textarea
              id="offer-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              placeholder="Why should the seller accept your offer?"
              className="rounded-md mt-1"
              data-testid="mp-offer-message-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={sending}
            className="rounded-md bg-[#B31E1E] hover:bg-[#8F1717] text-white font-semibold"
            data-testid="mp-offer-submit-btn"
          >
            {sending ? "Sending…" : "Send offer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  <ShippingQuoteWidget /> — buyer enters ZIP → gets USPS/UPS rates          */
/* -------------------------------------------------------------------------- */

export function ShippingQuoteWidget({ listing }) {
  const { user } = useAuth();
  const [destZip, setDestZip] = useState(user?.zip || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!listing || (listing.delivery_mode !== "shipping" && listing.delivery_mode !== "both")) return null;
  if (!listing.weight_oz) return null; // seller didn't set weight

  const fetchRates = async () => {
    const z = (destZip || "").replace(/\D/g, "").slice(0, 5);
    if (z.length < 5) return toast.error("Enter a 5-digit ZIP.");
    setLoading(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${listing.id}/shipping-quote`, { to_zip: z });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Card className="rounded-xl border border-[#EAE3DE] bg-white p-4 space-y-3" data-testid="shipping-quote-widget">
      <div className="flex items-center gap-2">
        <Truck size={14} className="text-[#B31E1E]" />
        <div className="text-sm font-semibold text-[#1B1614]">Estimate shipping</div>
      </div>
      <div className="flex gap-2">
        <Input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={destZip}
          onChange={(e) => setDestZip(e.target.value.replace(/\D/g, ""))}
          placeholder="Delivery ZIP"
          className="rounded-md"
          data-testid="shipping-zip-input"
        />
        <Button
          onClick={fetchRates}
          disabled={loading}
          variant="outline"
          className="rounded-md shrink-0"
          data-testid="shipping-quote-btn"
        >
          {loading ? "Fetching…" : "Get rates"}
        </Button>
      </div>
      {result?.rates?.length > 0 && (
        <div className="space-y-1" data-testid="shipping-rates-list">
          {result.rates.map((r, i) => (
            <div key={`${r.carrier}-${r.service}-${i}`} className="flex items-center justify-between text-[13px] px-2 py-1.5 rounded hover:bg-[#FAF5F2]">
              <span>
                <span className="font-semibold">{r.carrier}</span>{" "}
                <span className="text-[#5C4E48]">{r.service}</span>
                {r.delivery_days && <span className="text-[11px] text-[#8B7B73] ml-1.5">· {r.delivery_days}d</span>}
              </span>
              <span className="font-mono font-semibold">${r.rate_usd.toFixed(2)}</span>
            </div>
          ))}
          {result.warning && (
            <p className="text-[10px] text-[#8B7B73] italic mt-2">{result.warning}</p>
          )}
          {result.source === "easypost" && (
            <p className="text-[10px] text-emerald-700 mt-1">✓ Live carrier rates</p>
          )}
        </div>
      )}
    </Card>
  );
}
